<template>
  <aside v-if="activePanelConfig" class="viewer-side-panel" :class="positionClass">
    <header class="viewer-side-panel-header">
      <h2>{{ activePanelConfig.title }}</h2>
      <button class="delete" type="button" :aria-label="$t('button-close')" @click="$emit('close')"></button>
    </header>
    <section class="viewer-side-panel-body" :class="{ 'is-audit-log-panel': activePanel === 'audit-log' }">
      <algorithm-panel v-if="activePanel === 'algorithm'" :project="project" :index="activeImageIndex" />
      <ai-training-panel v-else-if="activePanel === 'ai-training'" :project="project" :index="activeImageIndex" />
      <pathology-report-panel v-else-if="activePanel === 'report'" @close="$emit('close')" />
      <annotation-audit-log-panel v-else-if="activePanel === 'audit-log'" :index="activeImageIndex" />
    </section>
  </aside>
</template>

<script>
import AlgorithmPanel from './AlgorithmPanel.vue';
import AiTrainingPanel from './AITrainingPanel.vue';
import PathologyReportPanel from './PathologyReportPanel.vue';
import AnnotationAuditLogPanel from './panels/AnnotationAuditLogPanel.vue';

export default {
  name: 'viewer-side-panel',
  components: {
    AlgorithmPanel,
    AiTrainingPanel,
    PathologyReportPanel,
    AnnotationAuditLogPanel
  },
  props: {
    activePanel: String,
    activeImageIndex: String,
    project: {
      type: Object,
      default: null
    },
    position: {
      type: String,
      default: 'right',
      validator: value => ['left', 'right'].includes(value)
    }
  },
  computed: {
    panelConfigs() {
      return {
        algorithm: {
          title: this.$t('ai-analysis')
        },
        'ai-training': {
          title: this.$t('ai-training')
        },
        report: {
          title: this.$t('histopathological-examination-report')
        },
        'audit-log': {
          title: this.$t('annotation-audit-logs')
        }
      };
    },
    activePanelConfig() {
      return this.panelConfigs[this.activePanel] || null;
    },
    positionClass() {
      return `is-${this.position}`;
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';
@import '../../assets/styles/viewer-layers';

$viewer-toolbar-width: 2.8rem;

.viewer-side-panel {
  position: absolute;
  top: 0;
  z-index: $viewer-z-toolbar-panel;
  display: flex;
  flex-direction: column;
  width: 600px;
  max-width: min(600px, 100%);
  height: 100%;
  background:
    linear-gradient(180deg, rgba(222, 238, 255, 0.04), rgba(222, 238, 255, 0.014)),
    $dark-bg-panel;
  color: $dark-text-primary;
  border-left: 1px solid rgba(190, 214, 238, 0.14);
  box-shadow: -18px 0 44px rgba(0, 0, 0, 0.28);

  &.is-right {
    right: 0;
    border-left: 1px solid rgba(190, 214, 238, 0.14);
    box-shadow: -18px 0 44px rgba(0, 0, 0, 0.28);
  }

  &.is-left {
    left: $viewer-toolbar-width;
    border-right: 1px solid rgba(190, 214, 238, 0.14);
    border-left: 0;
    box-shadow: 18px 0 44px rgba(0, 0, 0, 0.28);
  }
}

.viewer-side-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  gap: 1rem;
  min-height: 3.25rem;
  padding: 0.78rem 1rem;
  border-bottom: 1px solid rgba(190, 214, 238, 0.12);
  background: rgba(8, 17, 29, 0.38);

  h2 {
    min-width: 0;
    margin: 0;
    color: $dark-text-primary;
    font-size: 0.96rem;
    font-weight: 700;
    line-height: 1.25;
  }
}

.viewer-side-panel-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0.85rem;
}

.viewer-side-panel-body.is-audit-log-panel {
  overflow: hidden;
  padding: 0;
}

@media (max-width: 768px) {
  .viewer-side-panel {
    width: 100%;
    max-width: 100%;

    &.is-left {
      left: 0;
      width: calc(100% - #{$viewer-toolbar-width});
      max-width: calc(100% - #{$viewer-toolbar-width});
      margin-left: $viewer-toolbar-width;
    }
  }
}
</style>
